import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Textarea } from "../components/UI";
import { toast } from "sonner";
import { Palette, Loader2, Plus, Trash2, Upload, ShieldCheck, ImageIcon, FileVideo, FileText,
         Image as ImageLucide, Save, ExternalLink, ArrowLeft, ArrowRight, CheckCircle2, Circle,
         Sparkles, Lightbulb } from "lucide-react";

const REQUIRED_LABELS = {
  tagline: "Campaign tagline",
  bio: "Candidate bio",
  slogans: "At least one slogan",
  key_issues: "At least one key issue",
  disclaimer: "Compliance disclaimer",
  asset: "At least one uploaded asset (logo / photo)",
};

const ACCENT_PRESETS = ["#fbbf24", "#34d399", "#a78bfa", "#2dd4bf", "#fb7185", "#60a5fa", "#f87171", "#f97316"];
const HEX_OK = (v) => /^#[0-9a-fA-F]{6}$/.test(v || "");

const AccentColorPicker = ({ value, onChange }) => {
  const hex = HEX_OK(value) ? value : "#fbbf24";
  return (
    <div data-testid="brand-accent-picker">
      <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Accent color</label>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="relative w-10 h-10 rounded-lg border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: hex }} title="Pick a custom color">
          <input type="color" value={hex} onChange={(e) => onChange(e.target.value)}
                 className="absolute inset-0 opacity-0 cursor-pointer" data-testid="brand-accent-color-input" />
        </label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value.trim())} placeholder="#fbbf24" maxLength={7}
               className="w-28 bg-slate-900/60 border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-white focus:border-cyan-400 focus:outline-none"
               data-testid="brand-accent-hex-input" />
        <div className="flex gap-1.5 flex-wrap">
          {ACCENT_PRESETS.map((c) => (
            <button key={c} type="button" onClick={() => onChange(c)} title={c} data-testid={`brand-accent-preset-${c.slice(1)}`}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${hex.toLowerCase() === c ? "border-white" : "border-transparent"}`}
                    style={{ background: c }} />
          ))}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400" data-testid="brand-accent-preview">
        <span className="px-3 py-1 rounded-full font-semibold text-slate-900" style={{ background: hex }}>Contribute</span>
        <span>Live preview — used on your microsites, donation buttons and donor receipt emails.</span>
        {value && !HEX_OK(value) && <span className="text-amber-300">Enter a 6-digit hex like #fbbf24</span>}
      </div>
    </div>
  );
};

const BrandCompletenessCard = ({ completeness, onRecommend }) => {
  const c = completeness || { required: {}, complete: false, done_count: 0, total: 6 };
  const pct = Math.round(((c.done_count || 0) / (c.total || 6)) * 100);
  return (
    <Card padded={false} data-testid="brand-completeness">
      <div className="px-5 py-4 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              Brand Library completion
            </div>
            <div className="text-xs text-slate-400" data-testid="brand-completeness-progress">
              {c.done_count || 0} of {c.total || 6} required · {pct}%
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-3">
            <div
              className={`h-full transition-all ${c.complete ? "bg-gradient-to-r from-emerald-400 to-teal-500" : "bg-gradient-to-r from-cyan-400 to-blue-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(REQUIRED_LABELS).map(([key, label]) => {
              const done = !!(c.required || {})[key];
              return (
                <span
                  key={key}
                  data-testid={`brand-required-${key}`}
                  className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border ${
                    done
                      ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-300"
                      : "border-amber-400/30 text-amber-200"
                  }`}
                >
                  {done ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
                  {label}
                </span>
              );
            })}
          </div>
        </div>
        <button
          onClick={onRecommend}
          data-testid="brand-ai-recommend-btn"
          className="shrink-0 text-xs px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-400 to-pink-500 text-slate-900 font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
        >
          <Sparkles className="w-3.5 h-3.5" /> AI recommend from URL
        </button>
      </div>
    </Card>
  );
};

const KINDS = [
  { key: "logo", label: "Logo", icon: ImageLucide },
  { key: "photo", label: "Photo", icon: ImageIcon },
  { key: "video", label: "Video", icon: FileVideo },
  { key: "doc", label: "Doc / PDF", icon: FileText },
];

const usdIcon = ({ children }) => children;
void usdIcon;

// Convert File → data URL and downscale images to max 1280px width for size safety
const fileToDataUrl = (file, maxWidth = 1280) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Could not read file"));
  reader.onload = () => {
    const dataUrl = reader.result;
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") return resolve(dataUrl);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      if (scale >= 1) return resolve(dataUrl);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.85));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  };
  reader.readAsDataURL(file);
});

const ListEditor = ({ label, hint, items, onChange, testidPrefix, placeholder }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{label}</label>
      {hint && <div className="text-[11px] text-slate-500 mb-2">{hint}</div>}
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-list`}>
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs bg-white/[0.05] border border-white/10 text-slate-200"
                data-testid={`${testidPrefix}-item-${i}`}>
            {it}
            <button onClick={() => onChange(items.filter((_, k) => k !== i))}
                    className="w-4 h-4 rounded-full text-slate-500 hover:text-rose-400 inline-flex items-center justify-center"
                    aria-label="Remove">
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
               placeholder={placeholder}
               data-testid={`${testidPrefix}-input`}
               className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
        <button onClick={add} data-testid={`${testidPrefix}-add`}
                className="text-xs px-3 py-2 rounded-md border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
};

export default function BrandLibrary() {
  const navigate = useNavigate();
  const fromChecklist = new URLSearchParams(window.location.search).get("from") === "checklist";
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("photo");
  const [showRecommend, setShowRecommend] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const fileRef = useRef(null);

  const load = () => api.get("/political-os/brand-library")
    .then((r) => setData(r.data))
    .catch((e) => toast.error(errMsg(e, "Could not load brand library")));

  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  const bk = data.brand_kit || {};

  const update = (patch) => setData({ ...data, brand_kit: { ...bk, ...patch } });

  const save = async (opts) => {
    const goNext = opts && opts.goNext;
    setSaving(true);
    try {
      await api.put("/political-os/brand-library", bk);
      if (goNext) {
        toast.success("Brand library complete — on to the next step.");
        navigate("/onboarding/checklist");
        return;
      }
      toast.success("Saved — you can come back and finish the remaining items later.");
      if (fromChecklist) { navigate("/onboarding/checklist"); return; }
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const aiSuggestAssets = async () => {
    setAiBusy(true);
    toast.info("Generating 2 on-brand visuals — takes about 30 seconds…");
    try {
      const { data } = await api.post("/political-os/brand-library/ai-suggest-assets");
      setAiSuggestions(data.suggestions || []);
    } catch (e) { toast.error(errMsg(e, "AI asset generation failed")); }
    finally { setAiBusy(false); }
  };

  const addSuggestion = async (s, i) => {
    try {
      await api.post("/political-os/brand-library/assets", { kind: "photo", name: s.label, url: s.data_url });
      setAiSuggestions(list => list.filter((_, idx) => idx !== i));
      toast.success(`${s.label} added to your library`);
      load();
    } catch (e) { toast.error(errMsg(e, "Could not add the asset")); }
  };

  const upload = async (files) => {
    if (!files || !files.length) return;
    const file = files[0];
    if (file.size > 5_000_000) return toast.error("File too large (max 5 MB)");
    setUploading(true);
    try {
      const url = await fileToDataUrl(file);
      await api.post("/political-os/brand-library/assets", {
        kind, name: file.name.replace(/\.[^.]+$/, "").slice(0, 100), url,
      });
      toast.success(`${kind} uploaded`);
      load();
    } catch (e) { toast.error(errMsg(e, "Upload failed")); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAsset = async (id) => {
    try {
      await api.delete(`/political-os/brand-library/assets/${id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="brand-library-page">
      {fromChecklist && (
        <button onClick={() => navigate("/onboarding/checklist")} data-testid="brand-back-to-checklist"
          className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to launch checklist
        </button>
      )}
      <PageHeader eyebrow="Brand Brain" title="Campaign Asset Library"
        subtitle="One central place for bios, logos, photos, talking points and approved claims — auto-injected into every AI creative draft, email and ad."
        action={<Btn onClick={() => save()} disabled={saving} data-testid="brand-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save progress
        </Btn>}
      />

      {/* Required-fields tracker + AI recommend */}
      <BrandCompletenessCard
        completeness={data.completeness}
        onRecommend={() => setShowRecommend(true)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Identity */}
        <Card data-testid="brand-identity-card">
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Palette className="w-4 h-4 text-cyan-300" /> Identity
          </h3>
          <div className="space-y-3">
            <Input label="Campaign tagline" value={bk.tagline || ""}
                   onChange={(e) => update({ tagline: e.target.value })}
                   data-testid="brand-tagline"
                   placeholder="A stronger Texas for every family" />
            <Input label="Fundraising URL" value={bk.fundraising_url || ""}
                   onChange={(e) => update({ fundraising_url: e.target.value })}
                   data-testid="brand-fundraising-url"
                   placeholder="https://your-campaign.com/donate" />
            <AccentColorPicker value={bk.accent_color} onChange={(hex) => update({ accent_color: hex })} />
            <Input label="Voice / tone" value={bk.tone || ""}
                   onChange={(e) => update({ tone: e.target.value })}
                   data-testid="brand-tone"
                   placeholder="Warm, direct, no jargon" />
            <Textarea label="Candidate bio (used in AI drafts)" value={bk.bio || ""}
                      onChange={(e) => update({ bio: e.target.value })}
                      data-testid="brand-bio"
                      rows={4}
                      placeholder="2-4 sentence bio, background, and what you're running on…" />
            <Textarea label="Compliance disclaimer" value={bk.disclaimer || ""}
                      onChange={(e) => update({ disclaimer: e.target.value })}
                      data-testid="brand-disclaimer"
                      rows={2}
                      placeholder="Paid for by Committee to Elect Jane Smith" />
          </div>
        </Card>

        {/* Messaging */}
        <Card data-testid="brand-messaging-card">
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-cyan-300" /> Messaging
          </h3>
          <div className="space-y-4">
            <ListEditor label="Slogans" items={bk.slogans}
                        onChange={(v) => update({ slogans: v })}
                        testidPrefix="brand-slogans"
                        placeholder="Real leadership. Real results." />
            <ListEditor label="Key issues" items={bk.key_issues}
                        onChange={(v) => update({ key_issues: v })}
                        testidPrefix="brand-issues"
                        placeholder="Housing affordability" />
            <ListEditor label="Talking points" hint="Short punchy facts your ads should reinforce."
                        items={bk.talking_points}
                        onChange={(v) => update({ talking_points: v })}
                        testidPrefix="brand-tp"
                        placeholder="Cut property taxes by 12% in 2 years" />
            <ListEditor label="Approved CTAs" hint="Call-to-action lines the AI is allowed to use."
                        items={bk.ctas}
                        onChange={(v) => update({ ctas: v })}
                        testidPrefix="brand-ctas"
                        placeholder="Chip in $10 today" />
          </div>
        </Card>
      </div>

      {/* Asset upload */}
      <Card data-testid="brand-assets-card">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <Upload className="w-4 h-4 text-cyan-300" /> Uploaded assets
          </h3>
          <div className="flex flex-wrap gap-2 items-center" data-testid="brand-upload-controls">
            <div className="flex rounded-full border border-white/15 p-0.5">
              {KINDS.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setKind(key)}
                        data-testid={`brand-kind-${key}`}
                        className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1 transition ${kind === key ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40" : "text-slate-400 hover:text-white"}`}>
                  <Icon className="w-3 h-3" /> {label}
                </button>
              ))}
            </div>
            <input ref={fileRef} type="file"
                   accept={kind === "video" ? "video/*" : kind === "doc" ? ".pdf,application/pdf" : "image/*"}
                   onChange={(e) => upload(e.target.files)}
                   data-testid="brand-file-input"
                   className="text-xs text-slate-300 file:mr-2 file:text-xs file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-gradient-to-r file:from-cyan-400 file:to-blue-500 file:text-slate-900 file:font-semibold" />
            <Btn variant="secondary" className="!px-3 !py-1.5 text-xs" onClick={aiSuggestAssets} disabled={aiBusy} data-testid="brand-ai-assets-btn">
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI suggestions
            </Btn>
            {uploading && <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />}
          </div>
        </div>
        {aiSuggestions.length > 0 && (
          <div className="mb-4 rounded-lg border border-violet-400/25 bg-violet-400/5 p-3" data-testid="brand-ai-suggestions">
            <div className="text-xs text-violet-200 mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AI-recommended assets from your campaign info — add the ones you like
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {aiSuggestions.map((s, i) => (
                <div key={i} className="rounded-lg border border-white/10 overflow-hidden bg-white/[0.02]" data-testid={`ai-asset-${i}`}>
                  <img src={s.data_url} alt={s.label} className="w-full aspect-square object-cover" />
                  <div className="p-2 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-300">{s.label}</span>
                    <button onClick={() => addSuggestion(s, i)} className="text-[11px] text-cyan-300 hover:underline" data-testid={`ai-asset-add-${i}`}>Add</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.assets.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            No assets uploaded yet. Upload logos, hero photos, or a signed compliance doc — they&apos;ll appear in Ad Studio automatically.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="brand-assets-grid">
            {data.assets.map((a) => (
              <div key={a.id} className="group relative aspect-square rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`brand-asset-${a.id}`}>
                {(a.kind === "logo" || a.kind === "photo") && a.url ? (
                  <img src={a.url} alt={a.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                    {a.kind === "video" && <FileVideo className="w-8 h-8" />}
                    {a.kind === "doc" && <FileText className="w-8 h-8" />}
                    <div className="text-[10px] uppercase tracking-wider">{a.kind}</div>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent px-2 py-1.5">
                  <div className="text-[10px] text-white truncate">{a.name}</div>
                </div>
                <button onClick={() => removeAsset(a.id)}
                        data-testid={`brand-asset-remove-${a.id}`}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-slate-900/80 text-slate-400 hover:text-rose-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Approved claims (read-only reference from Claims Vault) */}
      <Card data-testid="brand-claims-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-300" /> Approved claims
          </h3>
          <a href="/claims-vault" className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1" data-testid="brand-open-claims-vault">
            Open Claims Vault <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="text-[11px] text-slate-500 mb-3">Only claims marked <b>approved</b> in the Claims Vault flow into AI-generated ad copy.</div>
        {data.approved_claims.length === 0 ? (
          <div className="text-sm text-slate-500 py-3">No approved claims yet — add sourced claims in the Claims Vault.</div>
        ) : (
          <ul className="space-y-2" data-testid="brand-approved-claims-list">
            {data.approved_claims.slice(0, 20).map((c) => (
              <li key={c.id} className="text-xs bg-white/[0.02] border border-white/5 rounded-md px-3 py-2 flex items-start gap-2" data-testid={`brand-claim-${c.id}`}>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-slate-200">{c.claim}</div>
                  {c.source_url && (
                    <a href={c.source_url} target="_blank" rel="noreferrer"
                       className="text-[10px] text-cyan-300 hover:underline">{c.source || c.source_url}</a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Next step gate */}
      <NextStepGate completeness={data.completeness} saving={saving} onSave={save} />

      {showRecommend && (
        <AIRecommendModal
          onClose={() => setShowRecommend(false)}
          onApply={(patch) => { setData({ ...data, brand_kit: { ...bk, ...patch } }); setShowRecommend(false); }}
        />
      )}
    </div>
  );
}

const NextStepGate = ({ completeness, saving, onSave }) => {
  const c = completeness || { required: {}, complete: false };
  const missing = Object.entries(c.required || {})
    .filter(([, v]) => !v)
    .map(([k]) => REQUIRED_LABELS[k]);
  return (
    <Card data-testid="brand-next-step-gate" className={c.complete ? "border-emerald-400/30" : ""}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          {c.complete ? (
            <>
              <div className="text-sm text-emerald-300 font-semibold inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> All required brand items complete
              </div>
              <div className="text-xs text-slate-400 mt-1">
                Your brand kit will now be auto-injected into every AI creative draft, receipt and microsite. On to the next launch step.
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-amber-300 font-semibold inline-flex items-center gap-1.5">
                <Circle className="w-4 h-4" /> {missing.length} required item{missing.length === 1 ? "" : "s"} left
              </div>
              <div className="text-xs text-slate-400 mt-1">
                You can save your progress and come back — the checklist won&apos;t mark this step done until every required item is filled in.
                <span className="block mt-1 text-slate-500">Still needed: {missing.join(" · ")}</span>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Btn onClick={() => onSave()} disabled={saving} variant="ghost" data-testid="brand-save-progress-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save progress
          </Btn>
          <Btn
            onClick={() => onSave({ goNext: true })}
            disabled={saving || !c.complete}
            data-testid="brand-next-step-btn"
            title={c.complete ? "Continue to the next launch step" : "Fill in every required item first"}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Continue to next step
          </Btn>
        </div>
      </div>
    </Card>
  );
};

const AIRecommendModal = ({ onClose, onApply }) => {
  const [url, setUrl] = useState("");
  const [socials, setSocials] = useState({ x: "", facebook: "", linkedin: "" });
  const [depth, setDepth] = useState("simple"); // "simple" | "deep"
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [picks, setPicks] = useState({});

  const run = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/political-os/brand-library/ai-recommend", {
        website_url: url.trim(),
        depth,
        socials: depth === "deep" ? socials : undefined,
      });
      setResult(data);
      // pre-select every non-empty suggestion
      const p = {};
      for (const k of Object.keys(data.suggestions || {})) if (data.suggestions[k]) p[k] = true;
      setPicks(p);
    } catch (e) {
      setErr(errMsg(e, "Could not analyze that URL — check it and try again."));
    } finally { setBusy(false); }
  };

  const apply = () => {
    const s = result?.suggestions || {};
    const patch = {};
    if (picks.tagline && s.tagline) patch.tagline = s.tagline;
    if (picks.bio && s.bio) patch.bio = s.bio;
    if (picks.slogans && s.slogans?.length) patch.slogans = s.slogans;
    if (picks.key_issues && s.key_issues?.length) patch.key_issues = s.key_issues;
    if (picks.disclaimer && s.disclaimer) patch.disclaimer = s.disclaimer;
    if (picks.tone && s.tone) patch.tone = s.tone;
    onApply(patch);
    toast.success("AI suggestions applied — remember to click Save progress.");
  };

  const S = result?.suggestions || {};
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="brand-recommend-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-300" />
            <h3 className="font-display text-lg font-bold text-white">AI brand recommend</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 overflow-y-auto space-y-4">
          {!result && (
            <>
              <div className="text-sm text-slate-300">
                Paste your campaign website — the AI will scrape it and suggest a tagline, bio, key issues, slogans, tone and a compliance disclaimer. Pick what to keep before it lands in your brand kit.
              </div>
              <Input label="Campaign website URL" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://janesmithforsenate.com" data-testid="brand-ai-url" />
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setDepth("simple")} data-testid="brand-ai-depth-simple"
                  className={`text-xs px-3 py-1.5 rounded-full border ${depth === "simple" ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400"}`}>
                  Simple (URL only)
                </button>
                <button onClick={() => setDepth("deep")} data-testid="brand-ai-depth-deep"
                  className={`text-xs px-3 py-1.5 rounded-full border ${depth === "deep" ? "border-fuchsia-400/50 bg-fuchsia-400/10 text-fuchsia-300" : "border-white/10 text-slate-400"}`}>
                  Deep (+ socials + FEC + web)
                </button>
              </div>
              {depth === "deep" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input label="X / Twitter handle" value={socials.x} onChange={(e) => setSocials({ ...socials, x: e.target.value })} placeholder="@candidate" />
                  <Input label="Facebook" value={socials.facebook} onChange={(e) => setSocials({ ...socials, facebook: e.target.value })} placeholder="facebook.com/…" />
                  <Input label="LinkedIn" value={socials.linkedin} onChange={(e) => setSocials({ ...socials, linkedin: e.target.value })} placeholder="linkedin.com/in/…" />
                </div>
              )}
              {err && <div className="text-xs text-rose-300">{err}</div>}
            </>
          )}
          {result && (
            <>
              <div className="text-xs text-slate-400">Uncheck anything you don&apos;t want to apply.</div>
              {[
                ["tagline", "Tagline", S.tagline],
                ["bio", "Bio", S.bio],
                ["disclaimer", "Compliance disclaimer", S.disclaimer],
                ["tone", "Tone", S.tone],
                ["slogans", "Slogans", (S.slogans || []).join(" · ")],
                ["key_issues", "Key issues", (S.key_issues || []).join(" · ")],
              ].filter(([, , v]) => !!v && v !== "").map(([k, label, v]) => (
                <label key={k} className="flex items-start gap-2 text-sm rounded-lg border border-white/10 p-3 hover:bg-white/[0.02] cursor-pointer" data-testid={`brand-ai-pick-${k}`}>
                  <input type="checkbox" checked={!!picks[k]} onChange={(e) => setPicks({ ...picks, [k]: e.target.checked })} className="mt-1" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">{label}</div>
                    <div className="text-slate-200 mt-0.5 break-words">{v}</div>
                  </div>
                </label>
              ))}
              {result?.source_notes && (
                <div className="text-[10px] text-slate-500 border-t border-white/5 pt-2 flex items-start gap-1.5">
                  <Lightbulb className="w-3 h-3 text-amber-300 shrink-0 mt-0.5" /> {result.source_notes}
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2" data-testid="brand-ai-cancel">Cancel</button>
          {!result ? (
            <Btn onClick={run} disabled={busy || !url.trim()} data-testid="brand-ai-run-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Analyzing…" : "Analyze"}
            </Btn>
          ) : (
            <Btn onClick={apply} data-testid="brand-ai-apply-btn">
              <CheckCircle2 className="w-4 h-4" /> Apply picked
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
};
